// src/components/portal/backupjobs.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import "./portal-components.css";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config/apiConfig";

import IconAdd from "./res/Icon-Add.svg";
import IconEdit from "./res/Icon-Edit.svg";
import IconTrash from "./res/Icon-Trash.svg";
import IconPlay from "./res/Icon-Play.svg";
import IconHistory from "./res/Icon-History.svg";

const STATUS_POLL_INTERVAL = 5000;

const BackupJobs = () => {
  const { user } = useAuth();
  
  const [jobs, setJobs] = useState([]);
  const [devices, setDevices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [authProfiles, setAuthProfiles] = useState([]);
  const [archiveProfiles, setArchiveProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const [formData, setFormData] = useState({
    job_name: "",
    description: "",
    target_type: "device",
    target_device_id: "",
    target_group_id: "",
    auth_profile_id: "",
    archive_profile_id: "",
    commands: ["show running-config"],
    schedule_type: "daily",
    schedule_time: "02:00",
    schedule_day_of_week: 0,
    schedule_day_of_month: 1
  });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [commandInput, setCommandInput] = useState("");

  const pollRef = useRef(null);

  // Fetch all data
  const fetchJobs = useCallback(async (showLoading = true) => {
    if (!user?.username) return;
    try {
      if (showLoading) setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/jobs?username=${encodeURIComponent(user.username)}`);
      const data = await res.json();
      if (data.status === "success") {
        setJobs(data.data?.jobs || []);
      }
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [user?.username]);

  const fetchDependencies = useCallback(async () => {
    if (!user?.username) return;
    
    try {
      // Fetch devices
      const devRes = await fetch(`${API_BASE_URL}/api/services/devices?username=${encodeURIComponent(user.username)}`);
      const devData = await devRes.json();
      if (devData.status === "success") setDevices(devData.data?.devices || []);
      
      // Fetch groups
      const grpRes = await fetch(`${API_BASE_URL}/api/services/groups?username=${encodeURIComponent(user.username)}`);
      const grpData = await grpRes.json();
      if (grpData.status === "success") setGroups(grpData.data?.groups || []);
      
      // Fetch auth profiles
      const authRes = await fetch(`${API_BASE_URL}/api/auth/profiles?username=${encodeURIComponent(user.username)}`);
      const authData = await authRes.json();
      if (authData.status === "success") setAuthProfiles(authData.data?.profiles || []);
      
      // Fetch archive profiles
      const archRes = await fetch(`${API_BASE_URL}/api/archives/profiles?username=${encodeURIComponent(user.username)}`);
      const archData = await archRes.json();
      if (archData.status === "success") setArchiveProfiles(archData.data?.profiles || []);
    } catch (err) {
      console.error("Failed to fetch dependencies:", err);
    }
  }, [user?.username]);

  useEffect(() => {
    fetchJobs();
    fetchDependencies();
  }, [fetchJobs, fetchDependencies]);

  // Poll for status updates
  useEffect(() => {
    pollRef.current = setInterval(() => {
      fetchJobs(false);
    }, STATUS_POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchJobs]);

  const handleSaveJob = async (e) => {
    e.preventDefault();
    setFormError("");
    
    if (!formData.job_name.trim()) {
      setFormError("Job name is required");
      return;
    }
    if (formData.target_type === 'device' && !formData.target_device_id) {
      setFormError("Please select a device");
      return;
    }
    if (formData.target_type === 'group' && !formData.target_group_id) {
      setFormError("Please select a device group");
      return;
    }
    if (!formData.auth_profile_id) {
      setFormError("Please select an authentication profile");
      return;
    }
    if (!formData.archive_profile_id) {
      setFormError("Please select an archive profile");
      return;
    }
    if (formData.commands.length === 0) {
      setFormError("At least one command is required");
      return;
    }
    
    setSaving(true);
    try {
      const endpoint = editingJob 
        ? `${API_BASE_URL}/api/jobs/${editingJob.id}`
        : `${API_BASE_URL}/api/jobs`;
      
      const res = await fetch(endpoint, {
        method: editingJob ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user.username, ...formData })
      });
      
      const data = await res.json();
      if (data.status === "success") {
        setShowForm(false);
        setEditingJob(null);
        resetForm();
        fetchJobs();
      } else {
        setFormError(data.reason || "Failed to save job");
      }
    } catch (err) {
      setFormError("Network error");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      job_name: "",
      description: "",
      target_type: "device",
      target_device_id: "",
      target_group_id: "",
      auth_profile_id: "",
      archive_profile_id: "",
      commands: ["show running-config"],
      schedule_type: "daily",
      schedule_time: "02:00",
      schedule_day_of_week: 0,
      schedule_day_of_month: 1
    });
    setCommandInput("");
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setFormData({
      job_name: job.job_name,
      description: job.description || "",
      target_type: job.target_type,
      target_device_id: job.target_device_id || "",
      target_group_id: job.target_group_id || "",
      auth_profile_id: job.auth_profile_id || "",
      archive_profile_id: job.archive_profile_id || "",
      commands: job.commands || ["show running-config"],
      schedule_type: job.schedule_type || "daily",
      schedule_time: job.schedule_time?.substring(0, 5) || "02:00",
      schedule_day_of_week: job.schedule_day_of_week || 0,
      schedule_day_of_month: job.schedule_day_of_month || 1
    });
    setShowForm(true);
    setFormError("");
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Delete job "${job.job_name}"?`)) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/jobs/${job.id}?username=${encodeURIComponent(user.username)}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchJobs();
      }
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleToggleJob = async (job) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/jobs/${job.id}/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user.username, enabled: !job.is_enabled })
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchJobs();
      }
    } catch (err) {
      console.error("Toggle failed:", err);
    }
  };

  const handleViewHistory = async (job) => {
    setShowHistoryModal(job);
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/jobs/${job.id}/runs?username=${encodeURIComponent(user.username)}&limit=20`);
      const data = await res.json();
      if (data.status === "success") {
        setJobHistory(data.data?.runs || []);
      }
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleAddCommand = () => {
    if (commandInput.trim()) {
      setFormData({...formData, commands: [...formData.commands, commandInput.trim()]});
      setCommandInput("");
    }
  };

  const handleRemoveCommand = (index) => {
    setFormData({...formData, commands: formData.commands.filter((_, i) => i !== index)});
  };

  const getScheduleText = (job) => {
    const time = job.schedule_time?.substring(0, 5) || "02:00";
    switch (job.schedule_type) {
      case 'daily': return `Daily at ${time}`;
      case 'weekly': 
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return `${days[job.schedule_day_of_week || 0]} at ${time}`;
      case 'monthly': return `Day ${job.schedule_day_of_month || 1} at ${time}`;
      case 'manual': return 'Manual';
      default: return job.schedule_type;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString();
  };

  const canCreateJob = devices.length > 0 && authProfiles.length > 0 && archiveProfiles.length > 0;

  return (
    <>
      <div className="right-top">
        <div className="right-top-title">Backup Jobs</div>
      </div>

      <div className="right-middle jobs-middle">
        <div className="service-section">
          <div className="cell service-cell">
            <div className="cell-header">
              <span>Scheduled Backup Jobs</span>
              <div className="header-actions">
                <button 
                  className="icon-btn add-btn" 
                  onClick={() => { setShowForm(true); setEditingJob(null); resetForm(); setFormError(""); }}
                  disabled={!canCreateJob}
                  title={!canCreateJob ? "Add devices, auth profiles, and archive profiles first" : ""}
                >
                  <img src={IconAdd} alt="Add" />
                  <span>Create Job</span>
                </button>
              </div>
            </div>
            <div className="cell-body">
              {!canCreateJob && (
                <div className="prereq-warning">
                  ⚠️ Before creating jobs, please configure:
                  {devices.length === 0 && <span className="prereq-item">• Service Connections (devices)</span>}
                  {authProfiles.length === 0 && <span className="prereq-item">• Authentication Profiles</span>}
                  {archiveProfiles.length === 0 && <span className="prereq-item">• Archive Profiles</span>}
                </div>
              )}
              
              {loading ? (
                <div className="loading">Loading jobs...</div>
              ) : jobs.length === 0 ? (
                <div className="empty-state">
                  <p>No backup jobs created</p>
                  <p className="empty-hint">Create a job to schedule automatic configuration backups</p>
                </div>
              ) : (
                <div className="jobs-list">
                  {jobs.map(job => (
                    <div key={job.id} className={`job-card ${!job.is_enabled ? 'disabled' : ''}`}>
                      <div className="job-header">
                        <div>
                          <div className="job-name">{job.job_name}</div>
                          <div className="job-target">
                            {job.target_type === 'device' 
                              ? `${job.target_device_name} (${job.target_device_ip})`
                              : `Group: ${job.target_group_name}`
                            }
                          </div>
                        </div>
                        <div className="job-toggle">
                          <label className="toggle-switch">
                            <input 
                              type="checkbox" 
                              checked={job.is_enabled}
                              onChange={() => handleToggleJob(job)}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                      </div>
                      
                      <div className="job-details">
                        <div className="job-detail">
                          <span className="job-detail-label">Schedule</span>
                          <span className="job-detail-value schedule-badge">{getScheduleText(job)}</span>
                        </div>
                        <div className="job-detail">
                          <span className="job-detail-label">Auth Profile</span>
                          <span className="job-detail-value">{job.auth_profile_name}</span>
                        </div>
                        <div className="job-detail">
                          <span className="job-detail-label">Archive Profile</span>
                          <span className="job-detail-value">{job.archive_profile_name}</span>
                        </div>
                        <div className="job-detail">
                          <span className="job-detail-label">Last Run</span>
                          <span className="job-detail-value">{formatDate(job.last_run_at)}</span>
                        </div>
                        {job.last_run_status && (
                          <div className="job-detail">
                            <span className="job-detail-label">Status</span>
                            <span className={`status-badge status-${job.last_run_status}`}>
                              <span className="status-dot"></span>
                              {job.last_run_status}
                            </span>
                          </div>
                        )}
                        <div className="job-detail">
                          <span className="job-detail-label">Next Run</span>
                          <span className="job-detail-value">{formatDate(job.next_run_at)}</span>
                        </div>
                      </div>
                      
                      <div className="job-actions">
                        <button className="btn-secondary" onClick={() => handleViewHistory(job)}>
                          <img src={IconHistory} alt="History" style={{ width: 14, height: 14, marginRight: 6 }} />
                          History ({job.total_runs || 0})
                        </button>
                        <button className="icon-btn-sm edit" onClick={() => handleEditJob(job)}>
                          <img src={IconEdit} alt="Edit" />
                        </button>
                        <button className="icon-btn-sm delete" onClick={() => handleDeleteJob(job)}>
                          <img src={IconTrash} alt="Delete" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* JOB FORM MODAL */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{editingJob ? "Edit Backup Job" : "Create Backup Job"}</h3>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSaveJob}>
              <div className="form-row">
                <label>Job Name *</label>
                <input 
                  type="text" 
                  value={formData.job_name} 
                  onChange={e => setFormData({...formData, job_name: e.target.value})} 
                  placeholder="e.g., Daily Router Backup" 
                />
              </div>

              <div className="form-row">
                <label>Target *</label>
                <div className="form-row-half">
                  <select 
                    value={formData.target_type}
                    onChange={e => setFormData({...formData, target_type: e.target.value, target_device_id: "", target_group_id: ""})}
                  >
                    <option value="device">Single Device</option>
                    <option value="group">Device Group</option>
                  </select>
                  {formData.target_type === 'device' ? (
                    <select 
                      value={formData.target_device_id}
                      onChange={e => setFormData({...formData, target_device_id: e.target.value})}
                    >
                      <option value="">Select device...</option>
                      {devices.map(d => (
                        <option key={d.id} value={d.id}>{d.hostname} ({d.ip_address})</option>
                      ))}
                    </select>
                  ) : (
                    <select 
                      value={formData.target_group_id}
                      onChange={e => setFormData({...formData, target_group_id: e.target.value})}
                    >
                      <option value="">Select group...</option>
                      {groups.map(g => (
                        <option key={g.id} value={g.id}>{g.group_name} ({g.device_count} devices)</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div className="form-row-half">
                <div className="form-row">
                  <label>Authentication Profile *</label>
                  <select 
                    value={formData.auth_profile_id}
                    onChange={e => setFormData({...formData, auth_profile_id: e.target.value})}
                  >
                    <option value="">Select profile...</option>
                    {authProfiles.map(p => (
                      <option key={p.id} value={p.id}>{p.profile_name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Archive Profile *</label>
                  <select 
                    value={formData.archive_profile_id}
                    onChange={e => setFormData({...formData, archive_profile_id: e.target.value})}
                  >
                    <option value="">Select profile...</option>
                    {archiveProfiles.map(p => (
                      <option key={p.id} value={p.id}>{p.profile_name} ({p.storage_type})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <label>Commands to Execute</label>
                <div className="commands-list">
                  {formData.commands.map((cmd, idx) => (
                    <div key={idx} className="command-item">
                      <code>{cmd}</code>
                      <button type="button" className="remove-cmd" onClick={() => handleRemoveCommand(idx)}>×</button>
                    </div>
                  ))}
                </div>
                <div className="add-command">
                  <input 
                    type="text" 
                    value={commandInput}
                    onChange={e => setCommandInput(e.target.value)}
                    placeholder="Enter command..."
                    onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddCommand())}
                  />
                  <button type="button" className="btn-secondary" onClick={handleAddCommand}>Add</button>
                </div>
              </div>

              <div className="form-row">
                <label>Schedule *</label>
                <div className="schedule-config">
                  <select 
                    value={formData.schedule_type}
                    onChange={e => setFormData({...formData, schedule_type: e.target.value})}
                    style={{ flex: 1 }}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="manual">Manual Only</option>
                  </select>
                  
                  {formData.schedule_type !== 'manual' && (
                    <input 
                      type="time" 
                      value={formData.schedule_time}
                      onChange={e => setFormData({...formData, schedule_time: e.target.value})}
                      style={{ width: 120 }}
                    />
                  )}
                  
                  {formData.schedule_type === 'weekly' && (
                    <select 
                      value={formData.schedule_day_of_week}
                      onChange={e => setFormData({...formData, schedule_day_of_week: parseInt(e.target.value)})}
                    >
                      <option value={0}>Sunday</option>
                      <option value={1}>Monday</option>
                      <option value={2}>Tuesday</option>
                      <option value={3}>Wednesday</option>
                      <option value={4}>Thursday</option>
                      <option value={5}>Friday</option>
                      <option value={6}>Saturday</option>
                    </select>
                  )}
                  
                  {formData.schedule_type === 'monthly' && (
                    <select 
                      value={formData.schedule_day_of_month}
                      onChange={e => setFormData({...formData, schedule_day_of_month: parseInt(e.target.value)})}
                    >
                      {[...Array(28)].map((_, i) => (
                        <option key={i+1} value={i+1}>Day {i+1}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div className="form-row">
                <label>Description (Optional)</label>
                <textarea 
                  value={formData.description} 
                  onChange={e => setFormData({...formData, description: e.target.value})} 
                  placeholder="Enter description..." 
                  rows="2" 
                />
              </div>

              {formError && <div className="form-error">{formError}</div>}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : "Save Job"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* HISTORY MODAL */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(null)}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Job History: {showHistoryModal.job_name}</h3>
              <button className="close-btn" onClick={() => setShowHistoryModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {loadingHistory ? (
                <div className="loading">Loading history...</div>
              ) : jobHistory.length === 0 ? (
                <div className="empty-state">
                  <p>No execution history yet</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Started</th>
                        <th>Completed</th>
                        <th>Status</th>
                        <th>Devices</th>
                        <th>Agent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobHistory.map(run => (
                        <tr key={run.id}>
                          <td className="date-cell">{formatDate(run.started_at)}</td>
                          <td className="date-cell">{formatDate(run.completed_at)}</td>
                          <td>
                            <span className={`status-badge status-${run.status}`}>
                              <span className="status-dot"></span>
                              {run.status}
                            </span>
                          </td>
                          <td>{run.devices_success}/{run.devices_total} success</td>
                          <td>{run.agent_hostname || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="form-actions">
              <button className="btn-primary" onClick={() => setShowHistoryModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div className="right-bottom-bar"></div>
    </>
  );
};

export default BackupJobs;
